<div class="drawer-form-wrapper">
  <app-student-info-card
    [student]="caseParams.case?.student"
    [supervisorName]="caseParams.case?.supervisorName">
  </app-student-info-card>

  <app-behavior-info-card
    [mode]="currentStep === 'guide' ? 'guide' : 'create'"
    [behaviorCategoryName]="caseParams.behavior?.behaviorCategoryName"
    [behaviorTypes]="caseParams.behavior?.behaviorCategoryTypes">
  </app-behavior-info-card>

  <ng-container *ngIf="isSelectionStep">
    <h4 class="title s-16 mb-16">Available Options</h4>
    <div class="form-grid w-40">
      <app-search-select
        label="Behavior strategy type"
        inputId="behaviorStrategy"
        [control]="typeControl"
        [useDefaultProactiveStrategy]="true"
        [defaultOption]="{
          id: +(typeControl.value ?? 0),
          value: '' + (typeControl.value ?? ''),
        }"
        lookupType="StrategyType"
        [optionOverflowSize]="7"
        placeholder="Search Behavior strategy type"
        noDataText="No Strategy type found"
        requiredErrorMessage="Please select Behavior strategy type">
      </app-search-select>
    </div>
    <div class="radio-wrapper mb-16">
      <nz-radio-group
        [ngModel]="selectedOption"
        (ngModelChange)="onOptionChange($event)"
        [ngModelOptions]="{ standalone: true }"
        name="selectedOption"
        [nzDisabled]="!isTypeSelected">
        <span nz-radio [nzValue]="'Guide me'" class="box-radio">
          Guide me
        </span>
        <span nz-radio [nzValue]="'Create my own'" class="box-radio">
          Create my own
        </span>
      </nz-radio-group>
    </div>

    <h3
      *ngIf="selectedOption === 'Create my own'"
      class="title s-18 mt-16 mb-12">
      Available District options
    </h3>

    <nz-table
      *ngIf="selectedOption === 'Create my own'"
      [nzData]="(publishStrategies$ | async) ?? []"
      [nzLoading]="loading$ | async"
      [nzFrontPagination]="false"
      [nzTotal]="(pubCount$ | async) ?? 0"
      [nzPageSize]="10"
      (nzQueryParams)="onPublishStrategyQueryParamsChange($event)"
      [nzScroll]="{ x: '1200' }">
      <thead>
        <tr>
          <th scope="col" nzWidth="60px"></th>
          <th
            scope="col"
            nzWidth="150px"
            [nzSortFn]="true"
            [nzColumnKey]="'title'">
            {{ strategyType || 'Strategy' }}
          </th>
          <th
            scope="col"
            nzWidth="300px"
            [nzSortFn]="true"
            [nzColumnKey]="'description'">
            Description
          </th>
        </tr>
      </thead>
      <tbody>
        @for (
          strategy of (publishStrategies$ | async) ?? [];
          track strategy.id
        ) {
          <tr>
            <td nzWidth="60px">
              <ng-container *ngIf="options.includes(5)">
                <div class="strategy-actions">
                  <nz-radio-group
                    [(ngModel)]="selectedId"
                    [ngModelOptions]="{ standalone: true }">
                    <span nz-radio [nzValue]="strategy.id"> </span>
                  </nz-radio-group>
                  <button
                    nz-button
                    type="button"
                    nzType="text"
                    (click)="openProactiveStrategyDrawer(strategy)">
                    <svg-icon
                      [title]="'View Strategy'"
                      src="/assets/icon/icon-arrow-right.svg"
                      class="icon s-12"
                      aria-label="Arrow Right"></svg-icon>
                  </button>
                </div>
              </ng-container>

              <ng-container *ngIf="!options.includes(5)">
                <div class="strategy-actions">
                  <span
                    nz-checkbox
                    [ngModel]="selectedIds.includes(strategy.id)"
                    (ngModelChange)="onCheckboxChange(strategy.id, $event)"
                    [ngModelOptions]="{ standalone: true }">
                  </span>
                  <button
                    nz-button
                    type="button"
                    nzType="text"
                    (click)="openProactiveStrategyDrawer(strategy)">
                    <svg-icon
                      [title]="'View Strategy'"
                      src="/assets/icon/icon-arrow-right.svg"
                      class="icon s-12"
                      aria-label="Arrow Right"></svg-icon>
                  </button>
                </div>
              </ng-container>
            </td>
            <td
              [title]="strategy.title"
              class="cursorPointer"
              (click)="openProactiveStrategyDrawer(strategy)"
              (keydown.enter)="openProactiveStrategyDrawer(strategy)">
              {{ strategy.title }}
            </td>
            <td [title]="strategy.description">{{ strategy.description }}</td>
          </tr>
        }
      </tbody>
    </nz-table>
  </ng-container>

  <ng-container *ngIf="isGuideStep">
    <nz-form-item>
      <nz-form-label>
        <p class="form-desc">
          The following list of {{ getStrategyTypeText() }} might be appropriate
          for addressing this challenging behavior. The list is organized from
          top to bottom based on how closely these suggested strategies match
          the defined behavior. Remember to modify each strategy to make sure it
          is individualized for the student and the environment in which it will
          be implemented. Also keep in mind that you can create your own
          strategies from scratch if you prefer.
        </p>
      </nz-form-label>
    </nz-form-item>

    <nz-table
      [nzData]="(strategies$ | async) ?? []"
      [nzFrontPagination]="false"
      [nzLoading]="loading$ | async"
      [nzTotal]="(stratCount$ | async) ?? 0"
      [nzPageSize]="guideParams$.value.pageSize"
      [nzPageIndex]="guideParams$.value.pageIndex"
      (nzQueryParams)="onGuideQueryParamsChange($event)"
      [nzScroll]="{ x: '1200' }">
      <thead>
        <tr>
          <th scope="col" nzWidth="70px"></th>
          <th scope="col" [nzSortFn]="true" [nzColumnKey]="'title'">
            {{ strategyType || 'Strategy' }}
          </th>
          <th scope="col" nzRight nzWidth="100px" nzAlign="center">Video</th>
        </tr>
      </thead>
      <tbody>
        @for (data of (strategies$ | async) ?? []; track data.id) {
          <tr>
            <td nzWidth="70px">
              <ng-container *ngIf="options.includes(5)">
                <div class="strategy-actions">
                  <nz-radio-group
                    [(ngModel)]="selectedId"
                    [ngModelOptions]="{ standalone: true }">
                    <span nz-radio [nzValue]="data.id"> </span>
                  </nz-radio-group>
                </div>
              </ng-container>
              <ng-container *ngIf="!options.includes(5)">
                <div class="strategy-actions">
                  <span
                    nz-checkbox
                    [ngModel]="selectedIds.includes(data.id)"
                    (ngModelChange)="onCheckboxChange(data.id, $event)"
                    [ngModelOptions]="{ standalone: true }">
                  </span>
                </div>
              </ng-container>
            </td>
            <td
              [title]="data.title"
              class="cursorPointer"
              (click)="openProactiveStrategyDrawer(data)"
              (keydown.enter)="openProactiveStrategyDrawer(data)">
              {{ data.title }}
            </td>
            <td nzRight nzWidth="50px">
              <div class="action-btn-wrapper">
                <button
                  *ngIf="data.videoUrl"
                  nz-button
                  type="button"
                  nzType="text"
                  class="action-btn"
                  (click)="openVideoUrl(data.videoUrl)">
                  <svg-icon
                    [title]="'Play Video'"
                    src="/assets/icon/icon-play.svg"
                    class="icon s-20"
                    aria-label="Play"></svg-icon>
                </button>
              </div>
            </td>
          </tr>
        }
      </tbody>
    </nz-table>
  </ng-container>

  <div class="display-flexustify-content-between drawer-footer">
    <div class="footer-left">
      <button nz-button type="button" nzType="default" (click)="onCancel()">
        Cancel
      </button>
    </div>
    <div class="footer-right flow-footer">
      <button nz-button type="button" nzType="default" (click)="onPrevious()">
        Previous
      </button>
      <button nz-button type="button" nzType="primary" (click)="onNext()">
        Next
      </button>
    </div>
  </div>
</div>

<app-video-modal [(visible)]="isVideoVisible" [videoUrl]="videoUrl">
</app-video-modal>

<ng-template #drawerBehaviorStrategies>
  <app-drawer-header
    [title]="'Manage Behavior Strategies'"
    [subtitle]="'Strategies'"
    [breadcrumbText]="caseParams.case?.student?.firstName || ''"
    (drawerClose)="closeManageBehaviorStrategiesDrawer()">
  </app-drawer-header
></ng-template>
